
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface DocumentType {
  id: string;
  title: string;
  description: string;
}

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: DocumentType | null;
  productId: string;
  onUploadSuccess: () => void;
}

export const DocumentUploadModal = ({ 
  isOpen, 
  onClose, 
  documentType, 
  productId, 
  onUploadSuccess 
}: DocumentUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file || !documentType) {
      toast.error("Please select a file to upload");
      return;
    }

    try {
      setIsUploading(true);
      
      // Mock upload - in a real app, this would upload to a storage service
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success("Document uploaded successfully");
      onUploadSuccess();
      onClose();
      setFile(null);
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>

        {documentType && (
          <div className="space-y-4 py-4">
            <div>
              <h3 className="font-medium">{documentType.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {documentType.description}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">Select File</Label>
              <Input 
                id="file" 
                type="file" 
                onChange={handleFileChange}
                disabled={isUploading}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              />
              <p className="text-xs text-muted-foreground">
                Accepted formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={isUploading || !file}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
